import * as React from "react"
import { Box, Button, Flex, Image, LinkBox, LinkOverlay, Text } from "@chakra-ui/react"

import { TutorialItemFragment } from "lib/graphql"
import { getVideoId } from "lib/helpers/utils"

import { TickIcon } from "./Icons"

interface Props {
  tutorial: TutorialItemFragment
}

export function TutorialTile({ tutorial }: Props) {
  return (
    <LinkBox
      position="relative"
      overflow="hidden"
      _hover={{ button: { display: "block" } }}
      _focusWithin={{
        boxShadow: "0 0 0 3px var(--chakra-colors-yellow-500)",
        outline: "none",
      }}
      _focusVisible={{
        boxShadow: "0 0 0 3px var(--chakra-colors-yellow-500)",
        outline: "none",
      }}
    >
      <LinkOverlay
        href={`/tutorials/${tutorial.id}`}
        textDecoration="none"
        aria-label={`View tutorial ${tutorial.title}`}
        tabIndex={0}
        _focus={{
          outline: "none",
        }}
      >
        <Flex w="100%" flexDir={{ base: "column", md: "row" }}>
          <Box position="relative">
            <Image
              src={`https://img.youtube.com/vi/${getVideoId(tutorial.videoUrl)}/0.jpg`}
              h={{ base: "250px", md: "195px" }}
              w={{ base: "100%", md: "350px" }}
              objectFit="cover"
              objectPosition="center"
              alt="evenement afbeelding"
              borderRadius="md"
              mr={8}
            />
            <Box
              position="absolute"
              top={{ base: "125px", md: "50%" }}
              transform="translate(-50%, -50%)"
              left={{ base: "50%", md: "calc(175px)" }}
            >
              <Button
                display="none"
                colorScheme={tutorial.hasCompleted ? "yellow" : "yellow"}
                textTransform="uppercase"
                _hover={{ bg: tutorial.hasCompleted ? "yellow.600" : "yellow.600" }}
              >
                {tutorial.hasCompleted ? "Play Again" : "Play"}
              </Button>
            </Box>
          </Box>
          <Flex
            py={3}
            flexDir="column"
            justifyContent="space-between"
            w={{ base: "100%", md: "calc(100% - 350px)" }}
          >
            <Text fontWeight="black" fontSize="3xl" textTransform="uppercase">
              {tutorial.title && tutorial.title}
            </Text>
            <Text
              mb="auto"
              fontSize="2xl"
              fontWeight="bold"
              color="gray.400"
              whiteSpace="nowrap"
              overflow="hidden"
              isTruncated
            >
              {tutorial.instructor && tutorial.instructor}
            </Text>
            <Flex justifyContent="space-between" w="100%" flexDir={{ base: "column", md: "row" }}>
              <Text fontWeight="bold" fontSize="lg" color="gray.400" display="flex" alignItems="center">
                <span
                  style={{
                    textTransform: "uppercase",
                    display: "inline-flex",
                    alignItems: "center",
                    paddingRight: "5px",
                  }}
                >
                  {tutorial.categories &&
                    `${tutorial.categories?.reduce((acc, i) => acc + i + " / ", "").slice(0, -2)}`}
                </span>
              </Text>
              {tutorial.difficultyRating && (
                <Flex alignItems="center">
                  <Text mr={4} color="gray.400" fontWeight={600} fontSize="lg">
                    Moeilijkheid:{" "}
                  </Text>
                  {Array.from(Array(5)).map((_, i) => {
                    return (
                      <Box
                        key={i}
                        borderRadius="50%"
                        height="12px"
                        width="12px"
                        background={
                          tutorial?.difficultyRating && tutorial?.difficultyRating >= i + 1
                            ? "yellow.500"
                            : "gray.400"
                        }
                        display="block"
                        marginRight="8px"
                      />
                    )
                  })}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
        {tutorial.hasCompleted && (
          <Box
            as={TickIcon}
            p={{ base: 2, md: 3.5 }}
            bg="yellow.500"
            borderRadius="50%"
            boxSize={{ base: "40px", md: "57px" }}
            color="black"
            pos="absolute"
            top={{ base: "15px", md: "24%" }}
            transform={{ base: undefined, md: "translateY(-50%)" }}
            right={{ base: "15px", md: 0 }}
            aria-label="Tutorial voltooid"
            role="img"
          />
        )}
      </LinkOverlay>
    </LinkBox>
  )
}
